<template>
  <article
    v-for="item in items"
    :key="`${item.id}_list`"
    class="content-wrapper"
  >
    <div class="content">
      <div class="item item-n1">
        <span class="t-h6">{{
          getCourseDate(item.start_date, item.end_date)
        }}</span>
        <span class="t-h6 wrap-text">{{ item.city }}</span>
      </div>
      <div class="item item-n2">
        <span class="t-form-text" v-html="nbsp(item.course.name)" />
        <span class="t-h6 title" v-html="nbsp(item.course.title)" />
      </div>
      <div class="item item-n3">
        <span class="t-form-text">{{ t('timetable.level') }}</span>
        <span class="t-p-m-130">
          {{
            t(`timetable.predefined-filter.${item.course.course_level}`)
          }}</span
        >
      </div>
      <div class="item item-n4">
        <span class="t-form-text">{{ t('timetable.participant_type') }}</span>
        <span class="t-p-m-130">
          {{ t(`timetable.predefined-filter.${item.course.participant_type}`) }}
        </span>
      </div>
      <div class="item item-n5">
        <span class="t-form-text">{{ t('timetable.teacher') }}:</span>
        <span class="t-p-m-130">{{ getTeachersFullnames(item.teachers) }}</span>
      </div>

      <div class="item item-n3-xl">
        <div class="inner-item">
          <span class="t-form-text">{{ t('timetable.level') }}</span>
          <span class="t-p-m-130">
            {{
              t(`timetable.predefined-filter.${item.course.course_level}`)
            }}</span
          >
        </div>
        <div class="inner-item">
          <span class="t-form-text">{{ t('timetable.participant_type') }}</span>
          <span class="t-p-m-130">
            {{
              t(`timetable.predefined-filter.${item.course.participant_type}`)
            }}
          </span>
        </div>

        <div class="inner-item">
          <span class="t-form-text">{{ t('timetable.teacher') }}:</span>
          <span class="t-p-m-130"
            >{{ getTeachersFullnames(item.teachers) }}
          </span>
        </div>
      </div>

      <div class="buttons-wrapper">
        <BaseButton
          tag="a"
          :href="item.register_url"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          :text="t('subscribe')"
        />
        <BaseButton
          :tag="NuxtLink"
          :to="
            useLocalePath()({
              name: 'course-slug-date',
              params: getSlugForCourseDate(item.full_slug)
            })
          "
          class="read-more-button"
          color="primary-grey"
          :text="t('read-more')"
        />
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import { NuxtLink } from '#components'
import type { PublicCourseAndEventDatePage } from '~/service/__generated-api'

const { t } = useI18n()

defineProps<{
  items: PublicCourseAndEventDatePage[]
}>()
</script>

<style lang="scss" scoped>
.content-wrapper {
  @include flex-col;
  position: relative;
  padding: rem(20px);
  background-color: $global-white;
  border-radius: $border-radius-small;
  box-shadow: $base-shadow;

  @include media-up(md) {
    padding-inline: rem(32px);
  }

  @include media-up(lg) {
    flex-direction: row;
    min-height: rem(84px);
  }
}

.content {
  @include flex-col;
  gap: rem(12px) rem(32px);
  width: 100%;

  @include media-up(md) {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
  }

  @include media-up(s-lg) {
    grid-template-columns: repeat(7, 1fr);
  }

  @include media-up(lg) {
    grid-template-rows: repeat(6, 1fr);
    grid-template-columns: repeat(12, 1fr);
    align-items: center;
    justify-content: space-between;
  }
}

.item {
  @include flex-col;

  &-n1 {
    @include media-up(md) {
      grid-row: 1 / 3;
      grid-column: 1 / 4;
      width: 100%;
      max-width: rem(230px);
    }
    @include media-up(s-lg) {
      grid-row: 1 / 4;
      grid-column: 1 / 3;
    }
    @include media-up(lg) {
      grid-row: 1 / 7;
      grid-column: 1 / 4;
    }
  }

  &-n2 {
    @include media-up(md) {
      grid-row: 1 / 3;
      grid-column: 3 / 7;
      margin-left: rem(12px);
    }
    @include media-up(s-lg) {
      grid-row: 1 / 4;
      grid-column: 3 / 7;
      margin-left: 0;
    }
    @include media-up(lg) {
      grid-row: 1 / 7;
      grid-column: 4 / 8;
    }
  }

  &-n3 {
    @include media-up(md) {
      grid-row: 3 / 5;
      grid-column: 1 / 2;
      width: 100%;
      min-width: rem(100px);
    }
    @include media-up(s-lg) {
      grid-row: 4 / 7;
      grid-column: 1 / 2;
    }
    @include media-up(lg) {
      display: none;
    }
  }

  &-n3-xl {
    display: none;
    flex: 1;
    grid-row: 1 / 7;
    grid-column: 8 / 12;

    @include media-up(lg) {
      display: flex;
    }

    .inner-item {
      display: flex;
      align-items: baseline;

      .t-form-text {
        flex-basis: rem(100px);
      }

      .t-p-m-130 {
        flex: 1;
      }
    }
  }

  &-n4 {
    @include media-up(md) {
      grid-row: 3 / 5;
      grid-column: 2 / 4;
    }
    @include media-up(s-lg) {
      grid-row: 4 / 7;
      grid-column: 2 / 3;
    }
    @include media-up(lg) {
      display: none;
    }
  }

  &-n5 {
    @include media-up(md) {
      grid-row: 3 / 5;
      grid-column: 3 / 7;
      margin-left: rem(12px);
    }
    @include media-up(s-lg) {
      grid-row: 4 / 7;
      grid-column: 3 / 7;
      margin-left: 0;
    }
    @include media-up(lg) {
      display: none;
    }
  }
}

.buttons-wrapper {
  display: flex;
  grid-row: auto;
  grid-column: 1 / 7;
  column-gap: rem(16px);

  @include media-up(md) {
    grid-row: 5 / 7;
    grid-column: 1 / 7;
  }
  @include media-up(s-lg) {
    flex-direction: column;
    grid-row: 1 / 7;
    grid-column: 7 / 8;
    gap: rem(12px);
    align-items: center;
    justify-content: center;
    margin-top: 0;
  }
  @include media-up(lg) {
    grid-row: 1 / 7;
    grid-column: auto;
  }

  :deep(.button) {
    width: rem(111px);
    height: rem(37px);
    font-size: 13px;
    font-weight: 600;
    line-height: 100%;
    text-align: center;
    letter-spacing: -0.13px;
  }
}

.title {
  margin-top: rem(4px);
}

.wrap-text {
  text-wrap: balance;
  word-wrap: break-word;
}

.read-more-button :deep(.text) {
  @include capitalize;
}
</style>
